/**
 * Phase RAG-4.5 — Super-admin tenant impersonation helper.
 *
 * Single source of truth for the `?as_tenant=<org_id>` query param that is
 * appended to every /api/rag/* call when a platform super-admin is operating
 * as a specific tenant.
 *
 * Source of truth precedence (matches backend require_tenant() rules):
 *   1. URL query string `?as_tenant=<org_id>` — canonical, shareable
 *   2. localStorage `qv_as_tenant` — convenience cache (survives reloads)
 *   3. null — no impersonation
 *
 * Tenant admins / retail users do NOT use this helper. The backend rejects
 * their impersonation attempts with 403 anyway.
 */

const STORAGE_KEY = 'qv_as_tenant';

/** Read impersonation target. Returns the org_id string or null. */
export function getActiveTenant() {
  // 1. URL takes precedence
  try {
    const sp = new URLSearchParams(window.location.search);
    const fromUrl = (sp.get('as_tenant') || '').trim();
    if (fromUrl) {
      // Cache for next page navigation
      try { localStorage.setItem(STORAGE_KEY, fromUrl); } catch (e) {}
      return fromUrl;
    }
  } catch (e) {}
  // 2. localStorage fallback
  try {
    const cached = (localStorage.getItem(STORAGE_KEY) || '').trim();
    if (cached) return cached;
  } catch (e) {}
  return null;
}

/** Persist a new active tenant + sync URL. */
export function setActiveTenant(orgId) {
  try {
    if (orgId) {
      localStorage.setItem(STORAGE_KEY, orgId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {}
}

/** Clear impersonation everywhere. */
export function clearActiveTenant() {
  setActiveTenant(null);
  try {
    const sp = new URLSearchParams(window.location.search);
    sp.delete('as_tenant');
    const newQs = sp.toString();
    const newUrl = window.location.pathname + (newQs ? ('?' + newQs) : '');
    window.history.replaceState(null, '', newUrl);
  } catch (e) {}
}

/**
 * Append `?as_tenant=<org_id>` to a URL when an impersonation target is set.
 * Backend enforces super-admin-only privilege; this helper just plumbs the
 * tenant tag through the URL.
 *
 * @param {string} url The URL (path or absolute) to decorate.
 * @returns {string}   URL with `?as_tenant=...` appended when an active tenant is set.
 */
export function withTenant(url) {
  const tenant = getActiveTenant();
  if (!tenant) return url;
  const sep = url.indexOf('?') >= 0 ? '&' : '?';
  return url + sep + 'as_tenant=' + encodeURIComponent(tenant);
}
